import StatusMessage from '../helpers/statusmessage.js';
import ErrorBox from '../helpers/errorbox.js';
import BusyButton from '../helpers/busybutton.js';

export default class Forms4i {
    constructor() {
        this.form = null;
        this.submitButton = null;
        this.errorBoxHelper = null;
        this.invalidClass = "input-validation-error";
        this.customValidation = null;
        this.fieldIsValid = true;
        this.dataVals = this.initializeDataVals();
        this.formObject = null;
        this.vmaskerPromise = null;

        this.passwordsJS = null;
        this.creditCardsJS = null;
        this.addressSuggestJS = null;
        this.addressValidationJS = null;
        this.recaptchaJS = null;
        this.onSuccess = null;
        this.onFailure = null;

        this.focusOnInput = this.focusOnInput.bind(this);
        this.validate = this.validate.bind(this);
        this.validateNoSubmit = this.validateNoSubmit.bind(this);
    }

    async init(config) {
        if(config == null || config.form == null){
            console.error("No configuration or form element found for your validation.");
            return;
        }
        const { 
            form, 
            submitButton = null,
            passwords = null, 
            creditCards = null, 
            addressSuggest = null, 
            addressValidate = null, 
            abandonment = null, 
            zipMasks = null, 
            phoneMasks = null, 
            customValidation = null, 
            recaptcha = null,
            onSuccess = null, 
            onFailure = null
        } = config;

        this.form = form;
        this.submitButton = submitButton;
        this.formObject = this.initializeFormObject();
        this.customValidation = customValidation;
        this.onSuccess = onSuccess;
        this.onFailure = onFailure;

        if(zipMasks && Array.isArray(zipMasks) && zipMasks.length > 0){
            //mask zip codes
            for(const zipInputID of zipMasks){
                await this.maskZipCode(document.getElementById(zipInputID));
            }
        }
        if(phoneMasks && Array.isArray(phoneMasks) && phoneMasks.length > 0){
            //mask phone numbers
            for(const phoneInputID of phoneMasks){
                await this.maskPhoneNumber(document.getElementById(phoneInputID));
                document.getElementById(phoneInputID).addEventListener("paste", (e) => {
                    this.fixPastedPhoneNumber(e, document.getElementById(phoneInputID));
                });
            }
        }

        // wire up submit button event listener automatically
        this.submitButton?.addEventListener("click", this.validate);

        //automatically import and initialize any extra JS that is needed
        if(abandonment) await this.getFormAbandonmentJS();
        if(addressSuggest) await this.getAddressSuggestJS();
        if(addressValidate) await this.getAddressValidateJS();
        if(recaptcha) await this.getRecaptchaJS();
        if(creditCards) await this.getCCJS();
        if(passwords) await this.getPasswordJS(); 
    }

    async getFormAbandonmentJS(){
        const { default: FormAbandonment4i } = await import('./formabandonment.js');
        FormAbandonment4i.init();
    }

    async getAddressSuggestJS(){
        const { default: AddressSuggest4i } = await import('./addresssuggest.js');
        this.addressSuggestJS = AddressSuggest4i;
        this.addressSuggestJS.init(this.form);
    }

    async getAddressValidateJS(){
        const { default: AddressValidation4i } = await import('./addressvalidation.js');
        this.addressValidationJS = new AddressValidation4i();
    }

    async getRecaptchaJS(){
        const { default: RecaptchaForm } = await import('./recaptcha.js');
        this.recaptchaJS = new RecaptchaForm();
        this.recaptchaJS.init(this.form);
    }

    async getCCJS(){
        const { default: CreditCards4i } = await import('./creditcards.js');
        this.creditCardsJS = new CreditCards4i();
        this.creditCardsJS.init(this.form);
    }

    async getPasswordJS(){
        const { default: Passwords4i } = await import('./passwords.js');
        this.passwordsJS = new Passwords4i();
        this.passwordsJS.init(this.form);
    }

    /**
     * Initializes validation data attributes.
     */
    initializeDataVals() {
        return {
            postalCode: "data-val-postalcodevalidation-required",
            postalCodeError: "data-val-postalcodevalidation",
            state: "data-val-statevalidation-required",
            stateError: "data-val-statevalidation",
            companyName: "data-val-companynamevalidation-required",
            companyError: "data-val-companynamevalidation",
            required: "data-val-required",
            regex: "data-val-regex-pattern",
            regexError: "data-val-regex",
            regexNotMatch: "data-val-regexnotmatch-pattern",
            regexNoMatchError: "data-val-regexnotmatch",
            maxLength: "data-val-length-max",
            minLength: "data-val-length-min",
            lengthError: "data-val-length",
            equalTo: "data-val-equalto-other",
            equalError: "data-val-equalto",
            password: "data-val-password-complexity-threshold",
            expiration: "data-validate-expiry",
        };
    }

    /**
     * Initializes the form object with relevant elements.
     */
    initializeFormObject() {
        const allInputs = Array.from(this.form.querySelectorAll("input"));
        return {
            element: this.form,
            heading: this.form.querySelector(".formHeading"),
            subHeadings: this.form.querySelectorAll(".formSubHeading"),
            textAreas: this.form.querySelectorAll("textarea"),
            selectBoxes: this.form.querySelectorAll("select"),
            inputAreas: allInputs.filter(
                (el) =>
                    !["submit", "radio", "checkbox"].includes(el.type.toLowerCase())
            ),
            radioChecks: allInputs.filter(
                (el) =>
                    ["radio", "checkbox"].includes(el.type.toLowerCase())
            ),
            requiredRadioChecks: [],
            errorMessages: this.form.querySelectorAll(".errorMessage"),
            fieldsToValidate: [],
            invalidFields: [],
            isValid: true,
            errorLines: "",
        };
    }

    calcCharacters(input, warningLength, maxLength) {
        input.addEventListener("keyup", () => {
            const hint = input.parentNode.querySelector(".hint");
            const charactersEntered = input.value.length;
            if(charactersEntered > warningLength){
                hint.classList.add("textRed");
            } 
            hint.textContent = (maxLength - charactersEntered) + " characters remaining";
        });
	}

    getErrorBoxLine(el, errorMsg) {
        const inputID = el ? el.id : "";
        return `<li><a href="#${inputID}" tabindex="0" role="link" class="errorItem">${errorMsg}</a></li>`;
    }

    buildErrorBoxLink(el, error) {
        this.formObject.errorLines += this.getErrorBoxLine(el, error);
    }

    async getFormHeading() {
        this.formObject.heading = this.form.getElementsByClassName("formHeading")[0];
        if (!this.formObject.heading) {
            this.form.insertAdjacentHTML('afterbegin', "<div class='formHeading'></div>");
            this.formObject.heading = this.form.querySelector(".formHeading");
        }
    }

    async getErrorBox() {
        let box = this.form.querySelector("#ErrorMsgBox");
        if (box) {
            box.innerHTML += this.formObject.errorLines;
        } else {
            box = await ErrorBox.createBox();
            this.formObject.heading.insertAdjacentHTML('afterend', box);
            document.getElementById("ErrorBox").querySelector("ul").innerHTML = this.formObject.errorLines;
        }
    }

     /**
     * Removes the error class from invalid fields.
     */
     removeErrorClass() {
        const invalidFields = this.form.querySelectorAll(`.${this.invalidClass}`);
        invalidFields.forEach((field) => field.classList.remove(this.invalidClass));
    }

     /**
     * Hides all error messages in the form.
     */
     hideErrorMessages() {
        if(this.formObject.errorMessages.length === 0) return;
        this.formObject.errorMessages.forEach((msg) => msg.classList.add("hide"));
    }

    async reorderErrors() {
        let formElements = Array.from(document.querySelectorAll("form input, form textarea, form select, div input, div textarea, div select"));
        let errorListLines = Array.from(document.getElementById("ErrorMsgBox").getElementsByTagName('li'));
        let newErrorListLines = [];

        errorListLines.forEach((elem) => {
            formElements.forEach((el) => {
                if (el.id === elem.firstChild.hash.replace("#", "")) {
                    newErrorListLines[formElements.indexOf(el)] = elem;
                }
            });
        });

        const filtered = newErrorListLines.filter(el => el !== null);
        const errorMsgBoxList = document.getElementById("ErrorMsgBox");
        while (errorMsgBoxList.firstChild) {
            errorMsgBoxList.removeChild(errorMsgBoxList.firstChild);
        }
        filtered.forEach((el) => errorMsgBoxList.appendChild(el) );
    }

    addErrorBoxEvents() {
        const errorListItems = Array.from(document.getElementsByClassName("errorItem"));
        errorListItems.forEach(item => {
            item.addEventListener("click", this.focusOnInput);
        });
    }

    focusOnInput(e){
        e.preventDefault();
        const el = e.currentTarget;
        const errorElem = document.getElementById(el.getAttribute("href").replace("#", ""));
        if (errorElem) {
            errorElem.scrollIntoView({ behavior: "smooth", block: "center" });
            errorElem.focus();
        }
    }

    /**
     * Adds an error box to the form.
     */
    async addErrorPanel() {
        await this.getFormHeading();
        await this.getErrorBox();

        if (this.formObject.requiredRadioChecks.length) {
            this.reorderErrors();
        }

        try {
            //todo use scrollandfocus module
            this.formObject.heading.scrollIntoView();
            setTimeout(() => {
                const errorPanel = this.form.querySelector(".ErrorMsgPanel");
                if (errorPanel && errorPanel.querySelector("a")) {
                    errorPanel.querySelector("a").focus();
                }
            }, 200);
        } catch (err) {
            console.error("Error while scrolling into view:", err);
        }

        this.addErrorBoxEvents();
    }

    showInvalidField(el, error) {
        el.classList.add(this.invalidClass);
        el.setAttribute("aria-invalid", "true");
        const errorMsg = el.parentNode.querySelector(".errorMessage");
        if (errorMsg) {
            errorMsg.innerHTML = error;
            errorMsg.classList.remove("hide"); 
        }
        this.fieldIsValid = false;
        this.buildErrorBoxLink(el, error);
    }

    async markFieldInvalid(el, error) {
        const box = await ErrorBox.createBox();
        this.form.querySelector(".formHeading").insertAdjacentHTML("afterend", box);
        if(el){
            this.showInvalidField(el, error); 
            document.querySelector("#ErrorBox ul").innerHTML = this.getErrorBoxLine(el, error);
            this.addErrorBoxEvents();
        } else {
            document.querySelector("#ErrorBox ul").innerHTML = this.getErrorBoxLine(null, error);
        }
    }

    closest(el, fn) {
        return el && (fn(el) ? el : this.closest(el.parentNode, fn));
    }

    async fixPastedPhoneNumber(e, input) {
        e.stopPropagation();
        e.preventDefault();

        var clipboardData = e.clipboardData || window.clipboardData; 
        input.value = clipboardData.getData('Text');
        
        await this.maskPhoneNumber(input);
    }

    async maskPhoneNumber(phoneNumElem = this.form.getElementById("PhoneNumber")) {
        const maskElement = document.getElementById("PhoneMask");
        if (!maskElement || !phoneNumElem || !maskElement.value) return;  // Guard clause: return early if essential elements are missing
    
        // only update the US & CA
        phoneNumElem.value = phoneNumElem.value.replace(/[+]/g, '');
        phoneNumElem.value = phoneNumElem.value.replace(/^1/, '');
        const masker = await this.loadVMasker();
        if(masker) masker(phoneNumElem).maskPattern(maskElement.value);
    }

    async maskZipCode(zipElem = this.form.getElementById("ZipCode")) {
        const maskElement = document.getElementById("ZipCodeMask");
        if (!maskElement || !zipElem || !maskElement.value) return;  // Guard clause: exit early if essential elements are missing
    
        const masker = await this.loadVMasker();
        if(masker) masker(zipElem).maskPattern(maskElement.value);
    }

    async unmaskElement(element) {
        const masker = await this.loadVMasker();
        if(masker && element) masker(element).unMask();
    }

    removeFieldValidation(input) {
        input.classList.remove(this.invalidClass);
        input.setAttribute("aria-invalid", "false");

        const errorMsg = input.parentNode ? input.parentNode.querySelector(".errorMessage") : null;
        errorMsg?.classList.add("hide");
    }

    /**
     * Resets the form validation state.
     */
    async reset(){
        this.removeErrorClass();
        this.removeErrorBox();
        this.hideErrorMessages();
        this.fieldIsValid = true;
        this.formObject.isValid = true;
        this.formObject.errorLines = "";
        this.formObject.errorMessages = [];
        this.formObject.fieldsToValidate = [];
        this.formObject.invalidFields = [];
    }

    async removeErrorBox() {
        ErrorBox.removeBox();
    }

    clearValidation() {
        this.removeErrorBox();
        const invalidFields = this.form.getElementsByClassName(this.invalidClass);
        Array.from(invalidFields).forEach((field) => this.removeFieldValidation(field));
    }

    // clears ALL form fields, including select boxes and text areas
    clearFormFields() {
        const formInputs = this.form.querySelectorAll("input, textarea");
        formInputs.forEach((input) => {
            if (input.value !== null && input.value !== "") input.value = "";
        });
    }

    async checkField(el) {
        this.fieldIsValid = true;

        //NOTE: please do not add any custom validation here that's only specific to your form
        //INSTEAD: pass in your customValidation through to the forms.FormValidation(); (see top of this doc or the styleguide for more info)

        if ((el.type.toLowerCase() === "checkbox" || el.type.toLowerCase() === "radio") && el.name !== "") {
            this.validateFieldset(el);
        } else if (el.value !== undefined && el.value.trim() === "") {
            this.validateEmptyInput(el);
        } else {
            this.validateRegexPattern(el);
            this.validateNoRegexPattern(el);
            this.validateMaxLength(el);
            this.validateMinLength(el);
            this.validateComparison(el);
            this.validateCCs(el);
            this.validatePasswords(el);
            this.validateCustomVals(el);
        }

        if (this.fieldIsValid) this.removeFieldValidation(el);
        else this.formObject.isValid = false;
    }

    validateFieldset(el) {
        const fieldset = this.closest(el, (elem) => {
                return elem.tagName.toLowerCase() === "fieldset";
        });
        const fieldsetInputs = [].slice.call(fieldset.querySelectorAll("input"));
        const lastInput = fieldsetInputs[fieldsetInputs.length - 1];
        if (el === lastInput){
            //none are checked
            if (!fieldsetInputs.some(e => e.checked)) {
                const errorMsg = fieldset.querySelector(".errorMessage") ? fieldset.querySelector(".errorMessage").innerHTML : "Please choose an option.";
                this.showInvalidField(el, errorMsg);
            }
        }
    }

    validateEmptyInput(el) {
        // List of required attributes and their corresponding error messages
        const validationRules = [
            { attribute: this.dataVals.postalCode, errorAttribute: this.dataVals.postalCodeError, defaultError: el.title },
            { attribute: this.dataVals.state, errorAttribute: this.dataVals.stateError, defaultError: el.title },
            { attribute: this.dataVals.companyName, errorAttribute: this.dataVals.companyError, defaultError: el.title }
        ];

        // Check if any required attribute is present
        for (const rule of validationRules) {
            const isRequired = el.hasAttribute(rule.attribute);
            if (isRequired && el.getAttribute(rule.attribute).toLowerCase() === "true") {
                this.showInvalidField(el, el.hasAttribute(rule.errorAttribute) ? el.getAttribute(rule.errorAttribute) : rule.defaultError);
                return; // Early exit after invalidating the field
            }
        }

        // If none of the specific attributes matched, check the general "required" attribute
        this.showInvalidField(el, el.hasAttribute(this.dataVals.required) ? el.getAttribute(this.dataVals.required) : el.title);
    }

    validateRegexPattern(el) {
        if ((el.pattern !== "" && el.value.search(el.pattern) < 0) || (el.hasAttribute(this.dataVals.regex) && el.value.search(el.getAttribute(this.dataVals.regex)) < 0))
            this.showInvalidField(el, el.hasAttribute(this.dataVals.regexError) ? el.getAttribute(this.dataVals.regexError) : el.title);
    }

    validateNoRegexPattern(el) {
        if (el.hasAttribute(this.dataVals.regexNotMatch) && el.value.toLowerCase().search(el.getAttribute(this.dataVals.regexNotMatch)) >= 0)
            this.showInvalidField(el, el.hasAttribute(this.dataVals.regexNoMatchError) ? el.getAttribute(this.dataVals.regexNoMatchError) : el.title);
    }

    validateMaxLength(el) {
        if (el.hasAttribute(this.dataVals.maxLength) && el.value.trim().length > parseInt(el.getAttribute(this.dataVals.maxLength)))
            this.showInvalidField(el, el.hasAttribute(this.dataVals.lengthError) ? el.getAttribute(this.dataVals.lengthError) : el.title);
    }

    validateMinLength(el) {
        if (el.hasAttribute(this.dataVals.minLength) && el.value.trim().length < parseInt(el.getAttribute(this.dataVals.minLength)))
            this.showInvalidField(el, el.hasAttribute(this.dataVals.lengthError) ? el.getAttribute(this.dataVals.lengthError) : el.title);
    }

    validateComparison(el) {
        if (el.hasAttribute(this.dataVals.equalTo) && document.getElementById(el.getAttribute(this.dataVals.equalTo)) !== null && document.getElementById(el.getAttribute(this.dataVals.equalTo)).value.trim() !== el.value.trim())
            this.showInvalidField(el, el.hasAttribute(this.dataVals.equalError) ? el.getAttribute(this.dataVals.equalError) : el.title);
    }

    async validateCCs(el) {
       if ((el.id == "PaymentMethod_CardNumber" || el.id == "CardNumber") || (el.hasAttribute(this.dataVals.expiration) && el.getAttribute(this.dataVals.expiration) && document.getElementById(el.getAttribute(this.dataVals.expiration)) !== null)) {
            if((el.id == "PaymentMethod_CardNumber" || el.id == "CardNumber")){
                const ccError = this.creditCardsJS.validateCardNumber(el);
                if(ccError){
                    formObject.isValid = false;
                    this.showInvalidField(el, ccError);
                } 
            }else{
                const expiryError = this.creditCardsJS.validateExpiry(el);
                if(expiryError){
                    formObject.isValid = false;
                    this.showInvalidField(el, expiryError);
                }   
            } 
        }
    }

    async validatePasswords(el) {
        if (el.hasAttribute(this.dataVals.password)) {
            const passwordError = await this.passwordsJS.validate(el);
            if(passwordError) this.showInvalidField(el, passwordError);
        }
    }

    validateCustomVals(el) {
        if (typeof this.customValidation === "function") {
            const customError = this.customValidation(el);
            if (customError) this.showInvalidField(el, customError);
        }
    }

    /**
     * Pushes fields to the validation queue.
     */
    async pushFieldsToValidate() {
        const { inputAreas, selectBoxes, textAreas, radioChecks } = this.formObject;

        inputAreas.forEach((el) => {
            if ((el.value.trim() || el.classList.contains("required")) && !el.disabled && el.type.toLowerCase() !== "hidden" && !el.classList.contains("hide")) {
                this.formObject.fieldsToValidate.push(el);
            }
        });

        selectBoxes.forEach((el) => {
            if ((el.value !== "0" && el.value !== "" || el.classList.contains("required")) && el.type.toLowerCase() !== "hidden" && !el.disabled && !el.classList.contains("hide")) {
                this.formObject.fieldsToValidate.push(el);
            }
        });

        textAreas.forEach((el) => {
            if ((el.value.trim() || el.classList.contains("required")) && el.type.toLowerCase() !== "hidden" && !el.disabled && !el.classList.contains("hide")) {
                this.formObject.fieldsToValidate.push(el);
            }
        });

        radioChecks.forEach((el) => {
            if (el.classList.contains("required") && !el.disabled && !el.classList.contains("hide")) {
                this.formObject.requiredRadioChecks.push(el);
                this.formObject.fieldsToValidate.push(el);
            }
        });
    }

    /**
     * Validates the form fields without any callbacks.
     * @param {function} callBack - Optional callback function to execute if validation passes.
     */
    async validateNoSubmit(callBack){
        StatusMessage.clear();
        
        try {
            await this.reset(); 
            await this.pushFieldsToValidate();

            for (const field of this.formObject.fieldsToValidate) {
                await this.checkField(field);
            }

            if (!this.formObject.isValid) {
                await this.addErrorPanel();
            }else{
                callBack && typeof callBack === "function" && await callBack();
            }
        } catch (err) {
            console.error("Form validation failed:", err);
        }
    }


    /*
    * Retrieves the submit button element.
    * @param {event} e - click event on submit button.
    * @returns {HTMLElement|null} - The submit button element or null if not found.
    */
    async getSubmitBtn(e){
        if(this.submitButton) return this.submitButton;
        
        if (e && e.target) {
            return e.target;
        }else if(e && e.currentTarget){
            return e.currentTarget;
        }else {
            return null;
        }
    }

    /*
        Submits the form with reCAPTCHA validation.
        If reCAPTCHA is not set up, it directly calls the onSuccess callback.
    */
    async submitSuccess(submitBtn){
        if(this.recaptchaJS){
            await this.recaptchaJS.ready(async () => {
                const recaptchaData = await this.recaptchaJS.fetchFormRecaptcha();
                if(recaptchaData && recaptchaData.token && typeof this.onSuccess === "function") 
                    await this.onSuccess(recaptchaData);
                else if(typeof this.onFailure === "function") 
                    await this.onFailure(submitBtn);
            });
        }else{
            if(typeof this.onSuccess === "function") await this.onSuccess(submitBtn);
        }
    }
        
    /**
     * Validates the form fields.
     * @param {event} e - click event on submit button.
     */
    async validate(e){
        if (e) {  e.preventDefault(); }
        StatusMessage.clear();
        const submitBtn = await this.getSubmitBtn(e);

        try {
            if(submitBtn) BusyButton.show(submitBtn);

            await this.reset(); 
            await this.pushFieldsToValidate();

            for (const field of this.formObject.fieldsToValidate) {
                await this.checkField(field);
            }

            if (this.formObject.isValid) {
                //if address suggest, then validate the address here
                // for success callback, pass in the submit button element in case it is needed
                // not required to use it.
                if(this.addressValidationJS) {
                    this.addressValidationJS.validateAddress(async () => { 
                        await this.submitSuccess(submitBtn);
                    });
                }else{
                    await this.submitSuccess(submitBtn);
                }
            } else {
                await this.addErrorPanel();
                //if(typeof this.onFailure === "function") await this.onFailure(submitBtn);
            }
        } catch (err) {
            console.error("Form validation failed:", err);
            if(typeof this.onFailure === "function") await this.onFailure(submitBtn);
        }finally {
            if (submitBtn) BusyButton.clear(submitBtn);
        }
    }

    async serverValidation() {
        StatusMessage.clear();
        try {
            const items = this.form.getElementsByTagName("li");
            let anchorTag, element, errorMessageElement;
    
            for (let i = 0; i < items.length; i++) {
                let el = items[i];
                let html = el.innerHTML;
    
                // Find anchor tag only once if it exists
                anchorTag = el.querySelector("a") ? el.querySelector("a") : null;
    
                // Locate the script part in the HTML using substring methods
                const start = html.indexOf("document.getElementById");
                if (start >= 0) {
                    const end = html.indexOf(")", start);
                    if (end > start) {
                        // Directly access the element without eval
                        const elementId = html.substring(start + 22, end - 1).trim(); // Extract the element ID
                        element = document.getElementById(elementId);  // Get element using the ID
    
                        if (element) {
                            // Apply invalid class and attributes
                            element.className += (element.className ? invalidClass : invalidClass);
                            element.setAttribute("aria-invalid", "true");
                            element.removeAttribute("tabindex");
    
                            // Show error message
                            errorMessageElement = element.parentNode.querySelector(".errorMessage");
                            if (errorMessageElement) {
                                errorMessageElement.classList.remove("hide");
    
                                // If anchor tag exists, update error message content
                                if (anchorTag) {
                                    errorMessageElement.innerHTML = anchorTag.innerHTML;
                                }
                            }
                        }
                    }
                }
            }
        } catch (e) {
            console.log(e.message);
        }
    }

    // Lazy load vanilla masker
    async loadVMasker() {
        if(this.vmaskerPromise){ return this.vmaskerPromise.default; }
        try {
            this.vmaskerPromise = await import('../../thirdparty/vmasker/vmasker.js');
            return this.vmaskerPromise.default; // Return the default export
        } catch (error) {
            console.error("Error in loadVMasker. Failed to load vanilla masker module:", error);
            this.vmaskerPromise = null; // Reset to allow retry on failure
            return null;
        }
    }
}